import { type FunctionComponent } from 'react';
import { formatCurrency } from '@/core/localization/currency';
import { translateFormatter } from '@/core/localization/translate';
import { Text } from '@/core/tamagoshi/Text/Text';
import { P } from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { CashbackPercentageLabel } from '@/domains/loyalty/modules/LoyaltyProgramHvc/components/CashbackPercentageLabel/CashbackPercentageLabel';
import { QuantitySelector } from '@/domains/productDiscovery/ProductPage/common/modules/QuantitySelector/QuantitySelector';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useQuantity } from '@/domains/productDiscovery/ProductPage/hooks/useQuantity';
import { type XBorderVat } from '@/domains/productDiscovery/ProductPage/hooks/useXBorderVat';
import { Disclaimer } from '@/productDiscovery/ProductPage/common/modules/Price/germanMentions/Disclaimer.default';
import { ProductPrice } from '@/productDiscovery/ProductPage/common/modules/Price/ProductPrice';
import { XBorderMentions } from '@/productDiscovery/ProductPage/common/modules/Price/XBorderVatMentions/XBorderMentions';
import { SurfaceInput } from '@/productDiscovery/ProductPage/common/modules/QuantitySelector/SurfaceInput';
import { useStock } from '@/productDiscovery/ProductPage/hooks/useStock';
import { outOfStock } from './translations';
import styles from './priceAndQuantity.module.scss';
export const priceEcoPart = translateFormatter<{
  price: string;
}>("Of which environmental contribution {price} including VAT.", undefined, undefined, "en");
interface Props {
  vatStatus?: XBorderVat;
}

// Used in the Legacy PDP
export const PriceAndQuantity: FunctionComponent<Props> = ({
  vatStatus
}) => {
  const {
    price,
    currency
  } = useDetail();
  const quantity = useQuantity();
  const stockValue = useStock();
  const isMaxStockReached = quantity === stockValue;
  return <div className={styles.container}>
      <SurfaceInput />

      <div className={styles.priceAndQuantity}>
        <ProductPrice vatStatus={vatStatus} />
        <div className="flex flex-col justify-between">
          <CashbackPercentageLabel />
          <QuantitySelector />
        </div>
      </div>

      <div className={styles.mentions}>
        <XBorderMentions vatStatus={vatStatus} />
      </div>

      <Disclaimer />

      {!!price?.ecoParticipationWithVat && <Text type="small" color="#505971" data-testid="ecopart">
          {priceEcoPart.format({
        price: formatCurrency(price.ecoParticipationWithVat, currency)
      })}
        </Text>}

      {isMaxStockReached && <div className={styles.text} data-testid="out-of-stock-reached">
          {outOfStock}
        </div>}
    </div>;
};
PriceAndQuantity.displayName = 'PriceAndQuantity';

// Used in the PDP Revamp
export const VatPrice: FunctionComponent<Props> = ({
  vatStatus
}) => {
  const {
    price,
    currency
  } = useDetail();
  return <div className="flex flex-col gap-s">
      <div className="flex flex-row gap-m">
        <div className="flex flex-row flex-wrap items-end justify-between gap-y-s">
          <ProductPrice vatStatus={vatStatus} />
        </div>
        <CashbackPercentageLabel />
      </div>

      <div className="self-start empty:hidden">
        <XBorderMentions vatStatus={vatStatus} />
      </div>

      <Disclaimer />

      {!!price?.ecoParticipationWithVat && <Text type="small" color="#505971" data-testid="ecopart">
          {priceEcoPart.format({
        price: formatCurrency(price.ecoParticipationWithVat, currency)
      })}
        </Text>}
    </div>;
};
VatPrice.displayName = 'VatPrice';

// Used in the PDP Revamp
export const MaxStockReached = () => {
  const quantity = useQuantity();
  const stockValue = useStock();
  const isMaxStockReached = quantity === stockValue;
  if (!isMaxStockReached) return null;
  return <P scale="body2">{outOfStock}</P>;
};
MaxStockReached.displayName = 'MaxStockReached';

// Used in the PDP Revamp
export const ProductQuantity: FunctionComponent = () => {
  return <div className="flex flex-col gap-m">
      <QuantitySelector className="!m-0 [&>div]:!h-[48px]" />
    </div>;
};
PriceAndQuantity.displayName = 'PriceAndQuantity';